import {
  ArrowLeftIcon,
  BookOpenIcon,
  CurrencyDollarIcon,
  InboxArrowDownIcon,
  MapPinIcon,
} from "@heroicons/react/24/solid"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import api2 from "../../../api2"
import ReportsDatePicker from "./ReportsDatePicker"
import { ArrowDownTrayIcon, TagIcon, AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline"
import Button from "../../ui/Button"
import { AdminContext } from "~/contexts/AdminContext"
import ChartsGrid from "./ChartsGrid"
import ReportButton from "../../ui/ReportButton"
import FilterBy from "./FilterBy"
import { reservationReportFilterCookie, addonReportFilterCookie, paymentsReportFilterCookie } from "~/constants/cookies"
import { useCookies } from "react-cookie"
import downloadCsv from "../../../utils/downloadCsv"
import Toggle from "~/components/ui/Toggle"
import { DateTime } from "luxon"
import { rentRollOptions } from "~/constants/reportIntervalOptions"
import { formatMoney } from "~/utils/formatMoney"
import { useReportsSitesOccupancy } from "~/components/Admin/Reports/ReportsHooks/useReportsSitesOccupancy"
import { useReportsReservationsVolume } from "~/components/Admin/Reports/ReportsHooks/useReportsReservationsVolume"
import { useReportsReservationsDashboard } from "~/components/Admin/Reports/ReportsHooks/useReportsReservationsDashboard"
import { useReportsSitesDashboard } from "~/components/Admin/Reports/ReportsHooks/useReportsSitesDashboard"
import { useReportsPaymentsDashboard } from "~/components/Admin/Reports/ReportsHooks/useReportsPaymentsDashboard"
import { useReportsRentRollDashboard } from "~/components/Admin/Reports/ReportsHooks/useReportsRentRollDashboard"
import { useReportsAddOnsDashboard } from "~/components/Admin/Reports/ReportsHooks/useReportsAddonsDashboard"
import { useReportsSites } from "~/components/Admin/Reports/ReportsHooks/useReportsSites"
import { useReportsAddOns } from "~/components/Admin/Reports/ReportsHooks/useReportsAddons"
import { useReportsReservations } from "~/components/Admin/Reports/ReportsHooks/useReportsReservations"
import { useReportsPayments } from "~/components/Admin/Reports/ReportsHooks/useReportsPayments"
import { useReportsRentRoll } from "~/components/Admin/Reports/ReportsHooks/useReportsRentRoll"
import classNames from "~/utils/classNames"
import useBreakpoint from "~/hooks/useBreakpoint"
import ReportsSites from "~/components/Admin/Reports/ReportsSites"
import ReportsReservations from "~/components/Admin/Reports/ReportsReservations"
import ReportsRentRoll from "~/components/Admin/Reports/ReportsRentRoll"
import ReportsProducts from "~/components/Admin/Reports/ReportsProducts"
import ReportsPayments from "~/components/Admin/Reports/ReportsPayments"
import { isMobile } from "react-device-detect"
import FilterDrawer from "~/ComponentsLibrary/FilterDrawer"
import { useReportsFilters } from "./hooks/useReportsFilters"

const getHeader = (slug: string) => {
  switch (slug) {
    case "reservations":
      return "Reservations Report"

    case "sites":
      return "Sites Report"

    case "rent-roll":
      return "Rent-Roll Report"

    case "products":
      return "Products Report"

    case "payments":
      return "Payments Report"

    default:
      return "Your Overview"
  }
}

type ReportsSlugType = "reservations" | "sites" | "rent-roll" | "products" | "payments"

type ReportsType = {
  name: "Reservations" | "Sites & Occupancy" | "Rent-Roll" | "Products" | "Payments"
  slug: ReportsSlugType
  stat?: string
  icon: Function
}

const reports: Array<ReportsType> = [
  {
    name: "Reservations",
    slug: "reservations",
    stat: "0 reservations",
    icon: BookOpenIcon,
  },
  {
    name: "Sites & Occupancy",
    slug: "sites",
    stat: "$0",
    icon: MapPinIcon,
  },
  {
    name: "Rent-Roll",
    slug: "rent-roll",
    stat: "$0",
    icon: InboxArrowDownIcon,
  },
  {
    name: "Products",
    slug: "products",
    stat: "$0",
    icon: TagIcon,
  },
  {
    name: "Payments",
    slug: "payments",
    stat: "$0",
    icon: CurrencyDollarIcon,
  },
]

const Reports = () => {
  const { camp, sites } = useContext(AdminContext)
  const [cookies, setCookie] = useCookies([
    reservationReportFilterCookie,
    addonReportFilterCookie,
    paymentsReportFilterCookie,
  ])
  const isMedium = useBreakpoint("(max-width: 768px)")
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<any[]>([])
  const [selectedReport, setSelectedReport] = useState<
    "reservations" | "sites" | "rent-roll" | "products" | "payments"
  >()
  const [updatedReports, setUpdatedReports] = useState<Array<ReportsType>>([])
  const [totalRecords, setTotalRecords] = useState<number>(0)

  const {
    filters,
    setSelectedStartDate,
    setSelectedEndDate,
    setTempStartDate,
    setTempEndDate,
    setPage,
    toggleDrawer,
    updateTempFilters,
    applyTempFilters,
    updateFilters
  } = useReportsFilters()

  const { data: reservationsVolumeData, isLoading: isLoadingReservationsVolume } = useReportsReservationsVolume(
    {
      campId: camp.id,
      startDate: filters.selectedStartDate,
      endDate: filters.selectedEndDate,
      filterBy: filters.selectedFilterByForReservation,
      page: filters.page,
    },
    { enabled: !selectedReport },
  )
  const { data: reservationsData, isLoading: isLoadingReservations } = useReportsReservations(
    {
      campId: camp.id,
      startDate: filters.selectedStartDate,
      endDate: filters.selectedEndDate,
      filterBy: filters.selectedFilterByForReservation,
      page: filters.page,
    },
    { enabled: selectedReport === "reservations" },
  )
  const { data: reservationsDashboardData, isLoading: isLoadingReservationsDash } = useReportsReservationsDashboard(
    {
      campId: camp.id,
      startDate: filters.selectedStartDate,
      endDate: filters.selectedEndDate,
      filterBy: filters.selectedFilterByForReservation,
    },
    { enabled: !selectedReport || selectedReport === "reservations" },
  )
  const { data: sitesDashboardData, isLoading: isLoadingSitesDash } = useReportsSitesDashboard(
    {
      campId: camp.id,
      startDate: filters.selectedStartDate,
      endDate: filters.selectedEndDate,
    },
    { enabled: !selectedReport || selectedReport === "sites" },
  )
  const { data: sitesData, isLoading: isLoadingSites } = useReportsSites(
    {
      campId: camp.id,
      startDate: filters.selectedStartDate,
      endDate: filters.selectedEndDate,
      page: filters.page,
    },
    { enabled: !selectedReport || selectedReport === "sites" },
  )
  const { data: sitesOccupancyData, isLoading: isLoadingSitesOccupancy } = useReportsSitesOccupancy(
    {
      campId: camp.id,
      startDate: filters.selectedStartDate,
      endDate: filters.selectedEndDate,
      sortBy: "start_date",
      page: 1,
    },
    { enabled: !selectedReport || selectedReport === "sites" },
  )
  const { data: paymentsData, isLoading: isLoadingPayments } = useReportsPayments(
    {
      campId: camp.id,
      startDate: filters.selectedStartDate,
      endDate: filters.selectedEndDate,
      filterBy: filters.selectedFilterByForPayments,
      showOnlyStripe: filters.showOnlyStripePayments,
      hideDeleted: filters.hideDeletedReservationPayments,
      page: filters.page,
    },
    { enabled: selectedReport === "payments" },
  )
  const { data: paymentsDashboardData, isLoading: isLoadingPaymentsDash } = useReportsPaymentsDashboard(
    {
      campId: camp.id,
      startDate: filters.selectedStartDate,
      endDate: filters.selectedEndDate,
      filterBy: filters.selectedFilterByForPayments,
      showOnlyStripe: filters.showOnlyStripePayments,
      hideDeleted: filters.hideDeletedReservationPayments,
    },
    { enabled: !selectedReport || selectedReport === "payments" },
  )
  const { data: rentRollDashboardData, isLoading: isLoadingRentRollDash } = useReportsRentRollDashboard(
    {
      campId: camp.id,
      startDate: filters.selectedStartDate,
      endDate: filters.selectedEndDate,
    },
    { enabled: !selectedReport || selectedReport === "rent-roll" },
  )
  const { data: rentRollData, isLoading: isLoadingRentRoll } = useReportsRentRoll(
    {
      campId: camp.id,
      startDate: filters.selectedStartDate,
      endDate: filters.selectedEndDate,
      page: filters.page,
    },
    { enabled: selectedReport === "rent-roll" },
  )
  const { data: addOnsDashboardData, isLoading: isLoadingAddOnsDash } = useReportsAddOnsDashboard(
    {
      campId: camp.id,
      startDate: filters.selectedStartDate,
      endDate: filters.selectedEndDate,
      filterBy: filters.selectedFilterByForAddon,
    },
    { enabled: !selectedReport || selectedReport === "products" },
  )
  const { data: addOnsData, isLoading: isLoadingAddOns } = useReportsAddOns(
    {
      campId: camp.id,
      startDate: filters.selectedStartDate,
      endDate: filters.selectedEndDate,
      filterBy: filters.selectedFilterByForAddon,
      page: filters.page,
    },
    { enabled: selectedReport === "products" },
  )

  useEffect(() => {
    const loadingStates = [
      isLoadingAddOns,
      isLoadingAddOnsDash,
      isLoadingPaymentsDash,
      isLoadingPayments,
      isLoadingRentRollDash,
      isLoadingReservations,
      isLoadingReservationsDash,
      isLoadingReservationsVolume,
      isLoadingSites,
      isLoadingSitesDash,
      isLoadingSitesOccupancy,
      isLoadingRentRoll,
    ]
    setIsLoading(loadingStates.some((state) => state))
  }, [
    isLoadingAddOns,
    isLoadingAddOnsDash,
    isLoadingPaymentsDash,
    isLoadingPayments,
    isLoadingRentRollDash,
    isLoadingReservations,
    isLoadingReservationsDash,
    isLoadingReservationsVolume,
    isLoadingSites,
    isLoadingSitesDash,
    isLoadingSitesOccupancy,
    isLoadingRentRoll,
  ])

  useEffect(() => {
    if (!selectedReport || !filters.selectedStartDate) {
      return
    }

    if (selectedReport == "sites" && sitesData) {
      setData(sitesData)
      setTotalRecords(sitesData["total_count"])
    } else if (selectedReport == "reservations" && reservationsData) {
      setData(reservationsData)
      setTotalRecords(reservationsData["total_count"])
    } else if (selectedReport == "products" && addOnsData) {
      setData(addOnsData)
      setTotalRecords(addOnsData["total_count"])
    } else if (selectedReport == "payments" && paymentsData) {
      setData(paymentsData)
      setTotalRecords(paymentsData["total_count"])
    } else if (selectedReport == "rent-roll" && rentRollData) {
      setData(rentRollData)
      setTotalRecords(rentRollData["total_count"])
    }
  }, [
    sitesData,
    reservationsData,
    addOnsData,
    paymentsData,
    rentRollData,
    selectedReport,
    filters.selectedEndDate,
    filters.selectedFilterByForReservation,
    filters.selectedFilterByForPayments,
    filters.selectedFilterByForAddon,
    filters.showOnlyStripePayments,
    filters.hideDeletedReservationPayments,
    filters.page,
  ])

  const fetchCSV = () => {
    const isEndDateValid = filters.selectedEndDate?.isValid

    const startDate = isEndDateValid ? filters.selectedStartDate : filters.selectedStartDate.set({}).startOf("day")
    const endDate = isEndDateValid ? filters.selectedEndDate : filters.selectedStartDate.set({}).endOf("day")

    if (selectedReport == "sites") {
      api2.Reports.sites_csv(camp.id, startDate, endDate, filters.page)
        .then((data) => {
          downloadCsv(camp.slug, data, "sites-report")
        })
        .catch((err) => {
          alert(JSON.stringify(err))
        })
    } else if (selectedReport == "rent-roll") {
      api2.Reports.rent_roll_csv(camp.id, startDate, endDate, filters.page)
        .then((data) => {
          downloadCsv(camp.slug, data, "rent-roll-report")
        })
        .catch((err) => {
          alert(JSON.stringify(err))
        })
    } else if (selectedReport == "reservations") {
      api2.Reports.reservations_csv(camp.id, startDate, endDate, filters.selectedFilterByForReservation, filters.page)
        .then((data) => {
          downloadCsv(camp.slug, data, "reservations-report")
        })
        .catch((err) => {
          alert(JSON.stringify(err))
        })
    } else if (selectedReport == "products") {
      api2.Reports.add_ons_csv(camp.id, startDate, endDate, filters.selectedFilterByForAddon, filters.page)
        .then((data) => {
          downloadCsv(camp.slug, data, "products-report")
        })
        .catch((err) => {
          alert(JSON.stringify(err))
        })
    } else if (selectedReport == "payments") {
      api2.Reports.payments_csv(
        camp.id,
        startDate,
        endDate,
        filters.selectedFilterByForPayments,
        filters.showOnlyStripePayments,
        filters.hideDeletedReservationPayments,
      )
        .then((data) => {
          downloadCsv(camp.slug, data, "payments-report")
        })
        .catch((err) => {
          alert(JSON.stringify(err))
        })
    }
  }

  const updateReports = (slug, data) => {
    if (!data) return

    let updatedStat = ""
    switch (slug) {
      case "reservations":
        updatedStat = `${data["amount_of_reservations"]} reservations`
        break
      case "sites":
        updatedStat = `${formatMoney(data["avg_site_revenue"], camp.currency)} per site`
        break
      case "rent-roll":
        updatedStat = `${formatMoney(data["total_monthly_rent"], camp.currency)} this month`
        break
      case "products":
        updatedStat = `${data["total_sold"]} sold`
        break
      case "payments":
        updatedStat = `${formatMoney(data["total_paid"], camp.currency)} net`
        break
      default:
        break
    }

    setUpdatedReports((prevReports) => {
      const reportIndex = prevReports.findIndex((r) => r.slug === slug)
      const newReports = [...prevReports]
      const originalReport = reports.find((r) => r.slug === slug)
      if (!originalReport) return prevReports
      const updatedReport = {
        ...originalReport,
        stat: updatedStat,
      }

      if (reportIndex >= 0) {
        newReports[reportIndex] = updatedReport
      } else {
        newReports.push(updatedReport)
      }

      return newReports
    })
  }

  useEffect(() => {
    if (reservationsDashboardData && filters.selectedStartDate) {
      updateReports("reservations", reservationsDashboardData)
    }
  }, [reservationsDashboardData, filters.selectedStartDate, filters.selectedEndDate])

  useEffect(() => {
    if (sitesDashboardData && filters.selectedStartDate) {
      updateReports("sites", sitesDashboardData)
    }
  }, [sitesDashboardData, filters.selectedStartDate, filters.selectedEndDate])

  useEffect(() => {
    if (rentRollDashboardData && filters.selectedStartDate) {
      updateReports("rent-roll", rentRollDashboardData)
    }
  }, [rentRollDashboardData, filters.selectedStartDate, filters.selectedEndDate])

  useEffect(() => {
    if (addOnsDashboardData && filters.selectedStartDate) {
      updateReports("products", addOnsDashboardData)
    }
  }, [addOnsDashboardData, filters.selectedStartDate, filters.selectedEndDate])

  useEffect(() => {
    if (paymentsDashboardData && filters.selectedStartDate) {
      updateReports("payments", paymentsDashboardData)
    }
  }, [paymentsDashboardData, filters.selectedStartDate, filters.selectedEndDate])

  const returnReport = useCallback(() => {
    switch (selectedReport) {
      case "reservations":
        return (
          <ReportsReservations
            data={data["data"]}
            summary={reservationsDashboardData}
            isLoading={filters.selectedStartDate && filters.selectedEndDate && (isLoadingReservations || isLoadingReservationsDash)}
          />
        )
      case "sites":
        return (
          <ReportsSites
            data={data["data"]}
            summary={sitesDashboardData}
            isLoading={
              filters.selectedStartDate && filters.selectedEndDate && (isLoadingSites || isLoadingSitesDash || isLoadingSitesOccupancy)
            }
          />
        )
      case "rent-roll":
        return (
          <ReportsRentRoll
            data={data["data"]}
            summary={rentRollDashboardData}
            isLoading={filters.selectedStartDate && filters.selectedEndDate && (isLoadingRentRoll || isLoadingRentRollDash)}
          />
        )
      case "products":
        return (
          <ReportsProducts
            data={data["data"]}
            summary={addOnsDashboardData}
            isLoading={filters.selectedStartDate && filters.selectedEndDate && (isLoadingAddOns || isLoadingAddOnsDash)}
          />
        )
      case "payments":
        return (
          <ReportsPayments
            data={data["data"]}
            summary={paymentsDashboardData}
            isLoading={filters.selectedStartDate && filters.selectedEndDate && (isLoadingPayments || isLoadingPaymentsDash)}
          />
        )
      default:
        break
    }
  }, [
    selectedReport,
    data,
    reservationsDashboardData,
    filters.selectedStartDate,
    filters.selectedEndDate,
    isLoadingReservations,
    sitesDashboardData,
    rentRollDashboardData,
    addOnsDashboardData,
    paymentsDashboardData,
    isLoadingPayments,
    isLoadingSites,
    isLoadingRentRoll,
    isLoadingAddOns,
  ])

  useEffect(() => {
    setPage(1)
  }, [
    filters.selectedFilterByForPayments,
    filters.showOnlyStripePayments,
    filters.hideDeletedReservationPayments,
    filters.selectedStartDate,
    filters.selectedEndDate,
  ])

  const loadingMap: { [key: string]: boolean } = useMemo(
    () => ({
      reservations: isLoadingReservationsDash,
      sites: isLoadingSitesDash,
      "rent-roll": isLoadingRentRollDash,
      products: isLoadingAddOnsDash,
      payments: isLoadingPaymentsDash,
    }),
    [isLoadingReservationsDash, isLoadingSitesDash, isLoadingRentRollDash, isLoadingAddOnsDash, isLoadingPaymentsDash],
  )

  const reportsToRender = useMemo(() => {
    return reports.map((originalReport) => {
      const updatedReport = updatedReports.find((r) => r.slug === originalReport.slug)

      const isLoading = loadingMap[originalReport.slug]

      const reportToShow = {
        ...originalReport,
        stat: !isLoading && updatedReport ? updatedReport.stat : undefined,
      }

      return {
        report: reportToShow,
        isLoading,
      }
    })
  }, [updatedReports, loadingMap])

  return (
    <div className={classNames(" w-screen relative", (isMedium && "px-10 pt-4") || "px-20 pt-2 pb-10")}>
      {selectedReport && (
        <div className="flex justify-between items-center relative">
          <Button
            variant="white"
            className="border-none h-11"
            icon={<ArrowLeftIcon className="w-5 h-5" />}
            onClick={() => setSelectedReport(null)}
          >
            Back
          </Button>
          <div className="text-lg sm:text-2xl pr-5 sm:pr-0 font-semibold">
            {getHeader(selectedReport)?.replace(isMobile ? " Report" : "", "")}
          </div>
          <div>
            {isMedium && selectedReport && (
              <div className="flex items-center gap-2">
                <Button
                  variant="white"
                  onClick={() => toggleDrawer(true)}
                  className="flex items-center border rounded-md p-2"
                >
                  <AdjustmentsHorizontalIcon className="h-6 w-6" />
                </Button>
                <Button variant="blue" className="sm:ml-auto flex-shrink-0" onClick={fetchCSV}>
                  <div className="flex items-center justify-center sm:justify-start">
                    <ArrowDownTrayIcon className={classNames("w-6 h-6 text-white")} />
                  </div>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={`flex flex-wrap gap-4 sticky top-0 bg-white z-[1] pt-2 justify-between ${selectedReport != null && selectedReport !== 'products' && "pl-2 md:pl-0 -mb-2 md:mb-0"}`}>
        {(!isMedium || !selectedReport) && (
          <ReportsDatePicker
            startDate={filters.selectedStartDate}
            setStartDate={setSelectedStartDate}
            endDate={filters.selectedEndDate}
            setEndDate={setSelectedEndDate}
            visible={true}
            options={selectedReport == "rent-roll" && rentRollOptions}
            isSinglePicker={selectedReport === "rent-roll"}
          />
        )}
        {selectedReport && !isMedium ? (
          <>
            {selectedReport === "reservations" && (
              <FilterBy
                defaultFilterBy={filters.selectedFilterByForReservation}
                setFilterBy={updateFilters("selectedFilterByForReservation")}
                options={[
                  { label: "Arrival Date", value: "start_date" },
                  { label: "Departure Date", value: "end_date" },
                  { label: "Created Date", value: "created_at" },
                ]}
                filterCookie={reservationReportFilterCookie}
              />
            )}
            {selectedReport === "products" && (
              <FilterBy
                defaultFilterBy={filters.selectedFilterByForAddon}
                setFilterBy={updateFilters("selectedFilterByForAddon")}
                options={[
                  { label: "Purchase Date", value: "purchase_date" },
                  { label: "Reservation Arrival Date", value: "start_date" },
                  { label: "Reservation Departure Date", value: "end_date" },
                  { label: "Reservation Created Date", value: "created_at" },
                ]}
                filterCookie={addonReportFilterCookie}
              />
            )}
            {selectedReport === "payments" && (
              <>
                <FilterBy
                  defaultFilterBy={filters.selectedFilterByForPayments}
                  setFilterBy={updateFilters("selectedFilterByForPayments")}
                  options={[
                    { label: "Cash-basis: when payment is collected", value: "revenue_booked" },
                    { label: "Accrual-basis: start date of reservation", value: "revenue_incurred" },
                  ]}
                  filterCookie={paymentsReportFilterCookie}
                />
                <Toggle
                  label="Show Only Stripe Payments"
                  checked={filters.showOnlyStripePayments}
                  onChange={updateFilters("showOnlyStripePayments")}
                />
                <Toggle
                  label="Show Removed Reservation Payments"
                  checked={!filters.hideDeletedReservationPayments}
                  onChange={(value) => updateFilters("hideDeletedReservationPayments")(!value)}
                />
              </>
            )}
          </>
        ) : (
          selectedReport && (
            <div className="relative">
              <FilterDrawer
                isOpen={filters.isDrawerOpen}
                onClose={() => {
                  updateTempFilters()
                  toggleDrawer(false)
                }}
                title="Filters"
                onApply={applyTempFilters}
              >
                <div className="space-y-4">
                  <ReportsDatePicker
                    startDate={filters.tempFilters.selectedStartDate}
                    setStartDate={setTempStartDate}
                    endDate={filters.tempFilters.selectedEndDate}
                    setEndDate={setTempEndDate}
                    visible={true}
                    options={selectedReport === "rent-roll" && rentRollOptions}
                    isSinglePicker={selectedReport === "rent-roll"}
                    isMobileDrawer={true}
                  />

                  {selectedReport === "reservations" && (
                    <FilterBy
                      key={filters.isDrawerOpen ? 'drawer-open' : 'drawer-closed'}
                      defaultFilterBy={filters.tempFilters.selectedFilterByForReservation}
                      setFilterBy={(value) => updateFilters('selectedFilterByForReservation', true)(value)}
                      options={[
                        { label: "Arrival Date", value: "start_date" },
                        { label: "Departure Date", value: "end_date" },
                        { label: "Created Date", value: "created_at" },
                      ]}
                      filterCookie={reservationReportFilterCookie}
                      position="top"
                    />
                  )}

                  {selectedReport === "products" && (
                    <FilterBy
                      key={filters.isDrawerOpen ? 'drawer-open' : 'drawer-closed'}
                      defaultFilterBy={filters.tempFilters.selectedFilterByForAddon}
                      setFilterBy={(value) => updateFilters('selectedFilterByForAddon', true)(value)}
                      options={[
                        { label: "Purchase Date", value: "purchase_date" },
                        { label: "Reservation Arrival Date", value: "start_date" },
                        { label: "Reservation Departure Date", value: "end_date" },
                        { label: "Reservation Created Date", value: "created_at" },
                      ]}
                      filterCookie={addonReportFilterCookie}
                      position="top"
                    />
                  )}

                  {selectedReport === "payments" && (
                    <>
                      <FilterBy
                        key={filters.isDrawerOpen ? 'drawer-open' : 'drawer-closed'}
                        defaultFilterBy={filters.tempFilters.selectedFilterByForPayments}
                        setFilterBy={(value) => updateFilters('selectedFilterByForPayments', true)(value)}
                        options={[
                          { label: "Cash-basis: when payment is collected", value: "revenue_booked" },
                          { label: "Accrual-basis: start date of reservation", value: "revenue_incurred" },
                        ]}
                        filterCookie={paymentsReportFilterCookie}
                        position="top"
                      />
                      <Toggle
                        label="Show Only Stripe Payments"
                        checked={filters.tempFilters.showOnlyStripePayments}
                        onChange={(value) => updateFilters('showOnlyStripePayments', true)(value)}
                      />
                      <Toggle
                        label="Show Removed Reservation Payments"
                        checked={!filters.tempFilters.hideDeletedReservationPayments}
                        onChange={(value) => updateFilters('hideDeletedReservationPayments', true)(!value)}
                      />
                    </>
                  )}
                </div>
              </FilterDrawer>
            </div>
          )
        )}
        {!isMedium && selectedReport && (
          <Button variant="blue" className="sm:ml-auto flex-shrink-0" onClick={fetchCSV}>
            <div className="flex items-center justify-center sm:justify-start">
              <span className="flex-shrink-0">Download report</span>
              <ArrowDownTrayIcon className={classNames("w-6 h-6 text-white", "ml-2")} />
            </div>
          </Button>
        )}
      </div>
      {selectedReport ? (
        <>
          {returnReport()}
          <nav className={classNames("flex items-center justify-between pt-4 pb-8")} aria-label="Pagination">
            {!isMedium && (
              <div className="pt-3">
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{filters.perPage * filters.page - filters.perPage + 1}</span> to{" "}
                  <span className="font-medium">{Math.min(filters.perPage * filters.page, totalRecords)}</span> of{" "}
                  <span className="font-medium">{totalRecords}</span> results
                </p>
              </div>
            )}

            <div className="flex flex-1 justify-between sm:justify-end items-center">
              {totalRecords > filters.perPage && (
                <Button
                  variant="none"
                  disabled={filters.page === 1}
                  onClick={() => {
                    if (filters.page > 1) setPage(filters.page - 1)
                  }}
                  className="w-28 justify-center relative inline-flex items-center rounded-md bg-white cursor-pointer px-3 py-2 text-md font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0"
                >
                  Previous
                </Button>
              )}
              {isMedium && (
                <p className="text-center text-sm text-gray-700 mx-1">
                  Showing <span className="font-medium">{filters.perPage * filters.page - filters.perPage + 1}</span> to{" "}
                  <span className="font-medium">{Math.min(filters.perPage * filters.page, totalRecords)}</span> of{" "}
                  <span className="font-medium">{totalRecords}</span> results
                </p>
              )}
              {totalRecords > filters.perPage && (
                <Button
                  variant="none"
                  disabled={filters.page > totalRecords / filters.perPage}
                  onClick={() => {
                    if (filters.page <= totalRecords / filters.perPage) setPage(filters.page + 1)
                  }}
                  className="w-28 justify-center relative inline-flex items-center rounded-md bg-white cursor-pointer px-3 py-2 text-md font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0"
                >
                  Next
                </Button>
              )}
            </div>
          </nav>
        </>
      ) : (
        <div className="w-full">
          <ChartsGrid
            startDate={filters.selectedStartDate}
            endDate={filters.selectedEndDate}
            sitesOccupancyData={sitesOccupancyData}
            reservationsVolumeData={reservationsVolumeData}
            numberOfSites={sites?.length}
            isLoading={isLoading}
          />
          <div className="text-1.5xl mt-8 mb-4 font-bold">
            Reports <span className="text-baselg font-normal text-gray-500 ml-1 italic">(click to view)</span>
          </div>
          <div className={classNames("grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-20 pb-10")}>
            {reportsToRender.map(({ report, isLoading }, id) => (
              <ReportButton
                loading={isLoading}
                placeholder={reports[id]}
                name={report.name}
                slug={report.slug}
                stat={report.stat}
                icon={report.icon}
                key={id}
                setSelectedReport={setSelectedReport}
                setPage={setPage}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default Reports
